import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VerifiedBadge from "../components/VerifiedBadge";
import WorkerBadge from "../components/WorkerBadge";
import WorkerProgressBar from "../components/WorkerProgressBar";
import StarRating from "../components/StarRating";
import AvatarUpload from "../components/AvatarUpload";
import LocationAutocomplete from "../components/LocationAutocomplete";
import { MessageCircle, Send, Mail, Copy, Calendar, Award, Briefcase, Pencil, X, Eye, EyeOff, Info, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function PublicProfile() {
  const { username } = useParams();
  const { user: authUser, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [ratings, setRatings] = useState([]);
  const [editOpen, setEditOpen] = useState(false);

  const isOwner = authUser?.username === username;
  const isManager = authUser?.role === "manager";
  const canSetFines = isOwner && (authUser?.role === "manager" || authUser?.role === "freelancer");
  const [fineIntroOpen, setFineIntroOpen] = useState(false);

  useEffect(() => {
    if (canSetFines && authUser && !authUser.seen_rating_intro_profile) {
      setFineIntroOpen(true);
      api.patch("/users/me", { seen_rating_intro_profile: true }).then(() => refresh()).catch(() => {});
    }
    // eslint-disable-next-line
  }, [authUser?.id]);
  const loadProfile = async () => {
    const r = await api.get(`/users/by-username/${username}`);
    setData(r.data);
    if (r.data?.user?.id) {
      api.get(`/ratings/${r.data.user.id}`).then(rr => setRatings(rr.data)).catch(() => {});
    }
  };

  useEffect(() => {
    loadProfile();
  }, [username]); // eslint-disable-line

  if (!data) return <div className="p-12 text-center text-muted-foreground">Loading…</div>;

  const { user, freelancer_profile, worker_stats } = data;
  const profileUrl = window.location.href;

  // Phone is available if: owner, manager, or user has show_phone=true
  const phoneAvailable = !!user.phone;
  // Email is available if: owner, or user has show_email=true
  const emailAvailable = !!user.email;

  const openWhatsApp = () => {
    if (phoneAvailable) {
      window.open(`https://wa.me/${user.phone.replace(/\D/g, "")}`, "_blank");
    } else {
      toast.error("This user hasn't made their phone number public");
    }
  };

  const openTelegram = () => {
    if (phoneAvailable) {
      window.open(`https://t.me/+${user.phone.replace(/\D/g, "")}`, "_blank");
    } else {
      toast.error("This user hasn't made their phone number public");
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(profileUrl);
    toast.success("Link copied!");
  };

  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-IN", { month: "long", year: "numeric" })
    : null;

  return (
    <div className="mx-auto max-w-3xl px-4 sm:px-6 py-8" data-testid="public-profile">

      {/* ── Header card ── */}
      <div className="card-flat flex items-start gap-5 flex-wrap">
        {isOwner
          ? <AvatarUpload size="lg" />
          : user.avatar
            ? <img src={user.avatar} alt="" className="w-20 h-20 rounded-full object-cover flex-shrink-0" />
            : <div className="w-20 h-20 rounded-full bg-brand-tint text-brand grid place-items-center font-display text-3xl flex-shrink-0">
                {(user.name || "?")[0]}
              </div>
        }

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <h1 className="font-display text-3xl tracking-tighter flex items-center gap-2 flex-wrap">
                                {user.name}
                {user.verified && <VerifiedBadge size={20} showLabel />}
                {worker_stats?.badge_tier && <WorkerBadge tier={worker_stats.badge_tier} size="lg" />}
              </h1>
              <p className="text-muted-foreground mt-0.5">
                @{user.username} · <span className="capitalize">{user.role}</span>
              </p>
            </div>
            {isOwner && (
              <button
                onClick={() => setEditOpen(o => !o)}
                className="btn-tint text-sm flex items-center gap-1.5"
              >
                {editOpen
                  ? <><X className="w-3.5 h-3.5" /> Cancel</>
                  : <><Pencil className="w-3.5 h-3.5" /> Edit profile</>
                }
              </button>
            )}
          </div>

          {user.bio && !editOpen && (
            <p className="text-sm text-foreground mt-2 leading-relaxed">{user.bio}</p>
          )}

          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            {user.rating && <StarRating rating={user.rating} count={user.rating_count} size="sm" />}
            {user.points != null && (
              <span className="flex items-center gap-1">
                <Award className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-amber-500 font-medium">{user.points} pts</span>
              </span>
            )}
            {memberSince && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3.5 h-3.5" /> Member since {memberSince}
              </span>
            )}
            {user.role === "manager" && data.jobs_posted?.length > 0 && (
              <span className="flex items-center gap-1">
                <Briefcase className="w-3.5 h-3.5" />
                {data.jobs_posted.length} job{data.jobs_posted.length !== 1 ? "s" : ""} posted
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Inline edit form (owner only) ── */}
      {isOwner && editOpen && (
        <div className="mt-4 card-flat">
          <h2 className="font-display text-xl mb-1">Edit profile</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Control what others can see on your public profile.
          </p>
          <ProfileEditForm
            user={authUser}
            onSave={async () => {
              await refresh();
              await loadProfile();
              setEditOpen(false);
            }}
          />
        </div>
      )}

      {/* ── Rating categories & fines (manager/freelancer, owner only) ── */}
      {canSetFines && (
        <div className="mt-4 card-flat">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display text-xl">Rating categories & fines</h2>
            <button onClick={() => setFineIntroOpen(true)} className="text-xs text-brand hover:underline flex items-center gap-1">
              <Info className="w-3.5 h-3.5" /> What's this?
            </button>
          </div>
          <p className="text-xs text-muted-foreground mb-4">
            Optional. Set up default rating categories and delay fines to reuse across your job posts.
          </p>
          <FineSettingsForm user={authUser} onSave={async () => { await refresh(); }} />
        </div>
      )}

      {/* ── Travel allowance settings (manager, owner only) ── */}
      {isOwner && isManager && (
        <div className="mt-4 card-flat">
          <h2 className="font-display text-xl mb-1">Travel allowance</h2>
          <p className="text-xs text-muted-foreground mb-4">
            Optional. Set your office location and a default rate per km to reuse across job posts. Workers are reimbursed for whichever is shorter — their home→job distance or your office→job distance.
          </p>
          <TravelExpenseSettingsForm user={authUser} onSave={async () => { await refresh(); }} />
        </div>
      )}

      {/* ── Fine intro popup ── */}
      {fineIntroOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={() => setFineIntroOpen(false)}>
          <div className="bg-card rounded-2xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="font-display text-xl mb-3">Rating categories & fines</h3>
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              This lets you set optional rules to fine workers for issues like uniform or punctuality — deducted from their payment.
            </p>
            <p className="text-sm text-muted-foreground mb-3 leading-relaxed">
              <strong>Star categories:</strong> rate a worker 0–5★ on things you define (e.g. Uniform). Each missing star costs the rate you set — e.g. ₹10/star, 3★ given = 2 stars short × ₹10 = ₹20 fine.
            </p>
            <p className="text-sm text-muted-foreground mb-5 leading-relaxed">
              This is completely optional and only applies if you turn it on for a job. Workers always see your fine policy before applying — no hidden fines.
            </p>
            <button onClick={() => setFineIntroOpen(false)} className="btn-primary w-full">Got it</button>
          </div>
        </div>
      )}

      {/* ── Contact buttons ── */}
      {/* Owner sees them as a preview of how their profile looks */}
      {isOwner && !editOpen && (
        <div className="mt-4">
          <p className="text-xs text-muted-foreground mb-2">
            Preview — this is how your contact buttons appear to others:
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={openWhatsApp}
              className={`btn-tint px-3 py-2 text-xs flex items-center gap-1.5 ${!phoneAvailable ? "opacity-40" : ""}`}
              title={!phoneAvailable ? "Phone hidden — toggle visibility in Edit profile" : ""}
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp
              {!phoneAvailable && <EyeOff className="w-3 h-3 ml-0.5" />}
            </button>
            <button
              onClick={openTelegram}
              className={`btn-tint px-3 py-2 text-xs flex items-center gap-1.5 ${!phoneAvailable ? "opacity-40" : ""}`}
              title={!phoneAvailable ? "Phone hidden — toggle visibility in Edit profile" : ""}
            >
              <Send className="w-4 h-4" /> Telegram
              {!phoneAvailable && <EyeOff className="w-3 h-3 ml-0.5" />}
            </button>
            {emailAvailable
              ? <a href={`mailto:${user.email}`} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
                  <Mail className="w-4 h-4" /> Email
                </a>
              : <button className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5 opacity-40" title="Email hidden — toggle visibility in Edit profile">
                  <Mail className="w-4 h-4" /> Email <EyeOff className="w-3 h-3 ml-0.5" />
                </button>
            }
            <button onClick={copyLink} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
              <Copy className="w-4 h-4" /> Copy link
            </button>
          </div>
        </div>
      )}

      {/* Non-owner visitors */}
      {!isOwner && (
        <div className="mt-4 flex flex-wrap gap-2">
          {phoneAvailable && (
            <>
              <button onClick={openWhatsApp} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </button>
              <button onClick={openTelegram} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
                <Send className="w-4 h-4" /> Telegram
              </button>
            </>
          )}
          {emailAvailable && (
            <a href={`mailto:${user.email}`} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
              <Mail className="w-4 h-4" /> Email
            </a>
          )}
          <button onClick={copyLink} className="btn-tint px-3 py-2 text-xs flex items-center gap-1.5">
            <Copy className="w-4 h-4" /> Copy link
          </button>
        </div>
      )}

      {/* ── Freelancer profile card ── */}
      {user.role === "freelancer" && freelancer_profile && (
        <div className="mt-6 card-flat flex items-center justify-between gap-3">
          <div>
            <p className="text-sm font-medium text-foreground">Freelancer profile</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {freelancer_profile.specializations?.slice(0, 3).join(", ") || "Available for hire"}
              {freelancer_profile.pricing ? ` · ${freelancer_profile.pricing}` : ""}
            </p>
          </div>
                    <a href={`/freelancer/${user.username}`} className="btn-primary text-sm whitespace-nowrap">
            View profile →
          </a>
        </div>
      )}
      {/* ── Worker progress + earnings ── */}
      {user.role === "worker" && worker_stats && (
        <div className="mt-6 card-flat">
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-medium text-foreground">Total earnings</p>
            <p className="font-display text-2xl font-black text-foreground">₹{worker_stats.total_earnings}</p>
          </div>
          <p className="text-xs text-muted-foreground mb-4">{worker_stats.jobs_completed_count} jobs completed</p>
          <WorkerProgressBar count={worker_stats.jobs_completed_count} tier={worker_stats.badge_tier} />
        </div>
      )}

      {/* ── Reviews ── */}
      {ratings.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-2xl mb-4">Reviews ({ratings.length})</h2>
          <div className="space-y-3">
            {ratings.slice(0, 5).map(r => (
              <div key={r.id} className="card-flat">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="font-medium text-sm">{r.rater_name}</span>
                  <StarRating rating={r.stars} size="sm" />
                  {r.job_title && <span className="text-xs text-muted-foreground">· {r.job_title}</span>}
                </div>
                {r.comment && <p className="text-sm text-slate-600">{r.comment}</p>}
                <p className="text-xs text-muted-foreground mt-1">{new Date(r.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {ratings.length === 0 && (
        <div className="mt-8 card-flat text-center py-8">
          <p className="text-sm text-muted-foreground">No reviews yet.</p>
        </div>
      )}
    </div>
  );
}

// ── Profile edit form with visibility toggles ──
function ProfileEditForm({ user, onSave }) {
  const [form, setForm] = useState({
    name: user.name || "",
    username: user.username || "",
    bio: user.bio || "",
    phone: user.phone || "",
    show_phone: user.show_phone !== false, // default true
    show_email: user.show_email || false,  // default false
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/users/me", form);
      toast.success("Profile updated!");
      await onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4 max-w-md">
      <div>
        <label className="label-eyebrow text-muted-foreground">Full name</label>
        <input className="input-base mt-2" value={form.name}
          onChange={e => setForm({...form, name: e.target.value})} required />
      </div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Username</label>
        <div className="relative mt-2">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">@</span>
          <input className="input-base !pl-8" value={form.username}
            onChange={e => setForm({...form, username: e.target.value.toLowerCase().replace(/[^a-z0-9._]/g, "")})}
            required />
        </div>
        <p className="text-xs text-muted-foreground mt-1">Lowercase letters, numbers, . and _ only</p>
      </div>
      <div>
        <label className="label-eyebrow text-muted-foreground">Bio</label>
        <textarea rows={3} className="input-base mt-2" placeholder="Tell people about yourself…"
          value={form.bio} onChange={e => setForm({...form, bio: e.target.value})} />
      </div>

      {/* Phone with visibility toggle */}
      <div>
        <label className="label-eyebrow text-muted-foreground">Phone number</label>
        <input className="input-base mt-2" placeholder="+91 XXXX XXXXXX"
          value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} type="tel" />
        <button
          type="button"
          onClick={() => setForm(f => ({...f, show_phone: !f.show_phone}))}
          className={`mt-2 flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition ${
            form.show_phone
              ? "border-brand text-brand bg-brand/5"
              : "border-border text-muted-foreground"
          }`}
        >
          {form.show_phone
            ? <><Eye className="w-3.5 h-3.5" /> Phone visible to everyone</>
            : <><EyeOff className="w-3.5 h-3.5" /> Phone hidden from public</>
          }
        </button>
        <p className="text-xs text-muted-foreground mt-1">
          Managers can always see your phone number for staffing purposes.
        </p>
      </div>

      {/* Email with visibility toggle */}
      <div>
        <div className="flex items-center justify-between mt-1">
          <label className="label-eyebrow text-muted-foreground">Email on profile</label>
        </div>
        <button
          type="button"
          onClick={() => setForm(f => ({...f, show_email: !f.show_email}))}
          className={`mt-2 flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition ${
            form.show_email
              ? "border-brand text-brand bg-brand/5"
              : "border-border text-muted-foreground"
          }`}
        >
          {form.show_email
            ? <><Eye className="w-3.5 h-3.5" /> Email visible on profile</>
            : <><EyeOff className="w-3.5 h-3.5" /> Email hidden from profile</>
          }
        </button>
        <p className="text-xs text-muted-foreground mt-1">
          Your login email. Hidden by default.
        </p>
      </div>

      <button disabled={saving} className="btn-primary w-full">
        {saving ? "Saving…" : "Save changes"}
      </button>
    </form>
  );
}

// ── Travel allowance setup (reusable default: office location + rate per km) ──
function TravelExpenseSettingsForm({ user, onSave }) {
  const [location, setLocation] = useState(user.location || "");
  const [lat, setLat] = useState(user.lat ?? null);
  const [lng, setLng] = useState(user.lng ?? null);
  const [rate, setRate] = useState(user.travel_expense_rate ?? "");
  const [minKm, setMinKm] = useState(user.travel_expense_min_km ?? "");
  const [maxAmount, setMaxAmount] = useState(user.travel_expense_max_amount ?? "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", {
        location,
        lat,
        lng,
        travel_expense_rate: Math.max(0, parseFloat(rate) || 0),
        travel_expense_min_km: Math.max(0, parseFloat(minKm) || 0),
        travel_expense_max_amount: Math.max(0, parseFloat(maxAmount) || 0),
      });
      toast.success("Travel allowance settings saved!");
      await onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <label className="label-eyebrow text-muted-foreground">Office location</label>
        <div className="mt-2">
          <LocationAutocomplete
            value={location}
            onChange={(val) => { setLocation(val); setLat(null); setLng(null); }}
            onSelect={({ display, lat: l, lng: g }) => { setLocation(display); setLat(l); setLng(g); }}
            placeholder="Search your office location…"
          />
        </div>
        {lat != null && <p className="text-xs text-emerald-600 mt-1">✓ Location confirmed</p>}
        {lat == null && location && <p className="text-xs text-amber-500 mt-1">⚠ Please select a location from the dropdown</p>}
      </div>

      <div>
        <label className="label-eyebrow text-muted-foreground">Rate per km (₹)</label>
        <input
          type="number"
          min="0"
          className="input-base mt-2 max-w-[10rem]"
          placeholder="e.g. 5"
          value={rate}
          onChange={e => setRate(e.target.value)}
        />
      </div>

      <div>
        <label className="label-eyebrow text-muted-foreground">Minimum distance to qualify (km)</label>
        <input
          type="number"
          min="0"
          className="input-base mt-2 max-w-[10rem]"
          placeholder="e.g. 3"
          value={minKm}
          onChange={e => setMinKm(e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground mt-1">Workers closer than this won't receive a travel allowance.</p>
      </div>

      <div>
        <label className="label-eyebrow text-muted-foreground">Max travel allowance per worker (₹, optional)</label>
        <input
          type="number"
          min="0"
          className="input-base mt-2 max-w-[10rem]"
          placeholder="e.g. 200"
          value={maxAmount}
          onChange={e => setMaxAmount(e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground mt-1">Leave blank for no cap. Protects against very distant workers costing too much.</p>
      </div>

      <button onClick={save} disabled={saving} className="btn-primary w-full">
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

// ── Rating categories & delay fine setup (reusable default) ──
function FineSettingsForm({ user, onSave }) {
  const [categories, setCategories] = useState(user.fine_categories || []);
  const [notes, setNotes] = useState(user.fine_notes || "");
  const [saving, setSaving] = useState(false);

  const addCategory = () => setCategories(c => [...c, { name: "", rate_per_star: "" }]);
  const updateCategory = (i, field, val) => setCategories(c => c.map((cat, idx) => idx === i ? { ...cat, [field]: val } : cat));
  const removeCategory = (i) => setCategories(c => c.filter((_, idx) => idx !== i));

  const hasAnyFines = categories.length > 0;

  const save = async () => {
    if (hasAnyFines && !notes.trim()) {
      toast.error("Fine policy notes are required when you have categories set — workers need to know why they're being fined.");
      return;
    }
    setSaving(true);
    try {
      const cleanCategories = categories
        .filter(c => c.name.trim())
        .map(c => ({ name: c.name.trim(), rate_per_star: Math.max(0, parseFloat(c.rate_per_star) || 0) }));
      await api.patch("/users/me", {
        fine_categories: cleanCategories,
        fine_notes: notes.trim(),
      });
      toast.success("Rating categories saved!");
      await onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Star categories */}
      <div>
        <label className="label-eyebrow text-muted-foreground">Star categories</label>
        <div className="mt-2 space-y-2">
          {categories.map((cat, i) => (
            <div key={i} className="flex gap-2 items-center">
              <input
                className="input-base !py-2 text-sm min-w-0"
                style={{ flex: '2 1 0%' }}
                placeholder="e.g. Uniform"
                value={cat.name}
                onChange={e => updateCategory(i, "name", e.target.value)}
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">₹</span>
                <input
                type="number"
                min="0"
                className="input-base !py-2 text-sm"
                style={{ flex: '0 0 5rem' }}
                placeholder="per star"
                value={cat.rate_per_star}
                onChange={e => updateCategory(i, "rate_per_star", e.target.value)}
              />
              <button type="button" onClick={() => removeCategory(i)} className="text-muted-foreground hover:text-red-500">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addCategory} className="mt-2 text-xs text-brand hover:underline flex items-center gap-1">
          <Plus className="w-3.5 h-3.5" /> Add category
        </button>
      </div>
      
      {/* Notes */}
      <div>
        <label className="label-eyebrow text-muted-foreground">
          Fine policy notes {hasAnyFines && <span className="text-red-500">*</span>}
        </label>
        <textarea
          rows={3}
          className="input-base mt-2"
          placeholder="Explain your rules in plain language — e.g. Uniform must be all-black, reporting time is strict…"
          value={notes}
          onChange={e => setNotes(e.target.value)}
        />
        <p className="text-[11px] text-muted-foreground mt-1">Shown to workers on the job listing before they apply.</p>
      </div>

      <button onClick={save} disabled={saving} className="btn-primary w-full">
        {saving ? "Saving…" : "Save rating categories"}
      </button>
    </div>
  );
}